<template>
  <div class="p-2">
    <spinner :show="loading"/>
    <div class="row" v-if="!loading">

      <template v-if="enableContinuousAuction">
        <div class="col-md-6">
          <div class="row">
            <div class="col-md-12 col-lg-6">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Payment Terms') }}
                  </h5>
                  <b-button class="float-right p-0" variant="link"
                            @click="togglePaymentTermsModal(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <span v-if="payment_terms">{{ payment_terms }}</span>
                  <no-data-available v-else/>
                </div>
              </div>
            </div>
            <div class="mt-3 mt-lg-0 col-md-12 col-lg-6">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Settlement Days') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="toggleSettlementDaysModal(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <span v-if="settlement_days">{{ settlement_days }}</span>
                  <no-data-available v-else/>
                </div>
              </div>
            </div>
            <div class="col-md-12 mt-3">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Premiums') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="togglePremiumsModal(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <table v-if="premiums && premiums.length > 0" class="info-table table-sm table">
                    <tr>
                      <th class="border-top-0">
                        {{ $t('Percentage') }}
                      </th>
                      <th class="border-top-0" v-if="premiums.length > 1">
                        {{ $t('Over') }}
                      </th>
                    </tr>
                    <tr v-for="(premium, index) in premiums" :key="`premium-visual-tier-${index}`">
                      <td :colspan="premiums.length > 1 ? 1 : 2">{{ premium.percent }} %</td>
                      <td v-if="premiums.length > 1">{{
                          premium.amount_over | asCurrency(sale.currency.symbol)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Minimum premium') }}</td>
                      <td>{{ minimum_premium | asCurrency }}</td>
                    </tr>
                  </table>
                  <no-data-available v-else :height="5"/>
                </div>

              </div>
            </div>
            <div class="col-md-12 mt-3">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Increment Table') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="toggleIncrementsModal(true)">
                    <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <table class="info-table table table-sm" v-if="increments.length > 0">
                    <tr>
                      <th>
                        {{ $t('Increment') }}
                      </th>
                      <th>
                        {{ $t('Upto') }}
                      </th>
                    </tr>
                    <tr v-for="(increment, index) in increments" :key="`increment-row-${index}`">
                      <td>
                        {{ increment.increment | asCurrency(sale.currency.symbol) }}
                      </td>
                      <td>
                        {{ (increments.length - 1) === index ? $t('And Up') : increment.max }}
                      </td>
                    </tr>
                  </table>

                  <no-data-available v-if="increments.length === 0" :height="5"/>
                </div>
              </div>
            </div>
            <div v-if="reservation.lot" class="col-md-12 mt-3">
              <div class="card" v-if="reservation.lot">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Commissions') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="onCommissionsModalShowClick()">
                    <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 border-right">
                      <commissions-view-table :commissions="reservation.lot.financial_item.commission_tiers"
                                              :tax-rate="reservation.lot.financial_item.tax_rate"/>
                    </div>
                    <div class="col-md-6">
                      <table class="info-table table mb-0 table-sm">
                        <tr>
                          <td class="border-top-0 font-weight-bold">{{ $t('Minimum commission') }}</td>
                          <td class="border-top-0">{{ reservation.lot.financial_item.minimum_commission | asCurrency }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('L/D Warranty') }}</td>
                          <td>{{ reservation.lot.financial_item.insurance_rate | asPercentage }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('Entry Fee') }}</td>
                          <td>{{ reservation.lot.financial_item.lot_handling_charge | asCurrency }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('Rebate') }}</td>
                          <td>{{ reservation.lot.financial_item.rebate_rate | asPercentage }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 mt-3 mt-md-0">
          <div class="row mb-3">
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Timed Auction Settings') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="onShowAuctionSettingsModal()">
                    <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body timed-auction-settings-card-body">
                  <table class="table table-sm">
                    <tr>
                      <td class="font-weight-bold border-top-0">{{ $t('Featured') }}</td>
                      <td class="border-top-0">
                        <span class="badge bg-success text-white" v-if="sale.featured">{{ $t('Yes') }}</span>
                        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('After Sale End Date') }}</td>
                      <td>{{ sale.after_sale_end_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Clearance Start Date') }}</td>
                      <td>{{ sale.clearance_start_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Clearance End Date') }}</td>
                      <td>{{ sale.clearance_end_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Strict End Date') }}</td>
                      <td>{{ sale.strict_end_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Clearance Comment') }}</td>
                      <td>{{ sale.clearance_comment }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Viewing by Appointment') }}</td>
                      <td>
                    <span v-if="sale.view_by_appointment === 'yes_with_dates'">
                        {{ $t('Yes with dates') }}
                    </span>
                        <span v-else-if="sale.view_by_appointment === 'yes'"
                              class="badge bg-success text-white">
                        {{ $t('Yes') }}
                    </span>
                        <span v-else-if="sale.view_by_appointment === 'no'"
                              class="badge bg-danger text-white">
                        {{ $t('No') }}
                    </span>
                        <span v-if="!sale.view_by_appointment"></span>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Viewing Start Date') }}</td>
                      <td>{{ sale.viewing_start_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Viewing End Date') }}</td>
                      <td>{{ sale.viewing_end_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Private Viewing Start Date') }}</td>
                      <td>{{ sale.private_viewing_start_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Private Viewing End Date') }}</td>
                      <td>{{ sale.private_viewing_end_date | toDateTime }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Viewing Comment') }}</td>
                      <td>{{ sale.viewing_comment }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Stagger End Times') }}</td>
                      <td>
                      <span v-if="sale.has_delay===false" class="badge bg-danger text-white">
                        {{ $t('No') }}
                      </span>
                        <span v-else-if="sale.has_delay===null">
                        {{ $t('No \'Stagger End Times\'') }}
                      </span>
                        <table v-else>
                          <tr>
                            <td class="border-top-0 pl-0">{{ $t('By:') }}</td>
                            <td class="border-top-0">{{ lotDelaySecondsValue }}</td>
                          </tr>
                          <tr>
                            <td class="pl-0">{{ $t('Every:') }}</td>
                            <td>{{ sale.lot_delay_batch_size }}</td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Buy Now Cut Off') }}</td>
                      <td>{{ buyNowCutOffDaysValue }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Bidding Extension') }}</td>
                      <td>{{ biddingExtensionValue }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Max Total Delay') }}</td>
                      <td>{{ maxTotalDelayValue }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('Sales Taxes') }}</td>
                      <td>
                      <span v-if="sale.tax_required===false" class="badge bg-danger text-white">
                        {{ $t('No') }}
                      </span>
                        <span v-else-if="sale.tax_required===null">
                        {{ $t('No \'Sales Taxes\'') }}
                      </span>
                        <table v-else>
                          <tr>
                            <td class="border-top-0 pl-0">{{ $t('Hammer Tax Rate:') }}</td>
                            <td class="border-top-0">{{ !sale.hammer_tax_rate ? 0 : sale.hammer_tax_rate }}</td>
                          </tr>
                          <tr>
                            <td class="pl-0">{{ $t('BP Tax Rate:') }}</td>
                            <td>{{ !sale.bp_tax_rate ? 0 : sale.bp_tax_rate }}</td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                </div>
                <auction-settings-modal/>
              </div>
            </div>
          </div>
          <div class="row" v-if="reservation.lot">
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">{{ $t("Charges") }}</h5>
                </div>
                <div class="card-body">
                  <table class="table table-sm">
                    <tr>
                      <th>{{ $t("Title") }}</th>
                      <th>{{ $t("Amount") }}</th>
                      <th>{{ isSalesTaxEnabled ? $t('Sales Tax') : $t('VAT') }}</th>
                      <th></th>
                    </tr>
                    <tr v-for="(financial_item_charge,index) in reservation.lot.financial_item.financial_item_charges"
                        :key="`charge-row-${index}`">
                      <td>
                        {{ financial_item_charge.charge.name }}
                      </td>
                      <td>
                        {{ financial_item_charge.amount }}
                      </td>
                      <td>
                        {{ financial_item_charge.vat }}
                      </td>
                      <td class="text-right">

                        <b-tooltip :target="`charge-edit-btn-${financial_item_charge.uuid}`"
                                   :title="$t('Edit Charge')"/>
                        <b-button :id="`charge-edit-btn-${financial_item_charge.uuid}`" variant="primary" size="sm"
                                  class="mr-2"
                                  @click="onChargeEditClick(financial_item_charge)" v-permission="'change_auction'">
                          <font-awesome-icon icon="pen"></font-awesome-icon>
                        </b-button>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-3" v-if="reservation.lot">
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Tax Status') }}
                  </h5>
                  <b-button class="float-right p-0" variant="link"
                            @click="onShowTaxStatusModalClick(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <span>{{ currentTaxStatusName }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-12 mt-3">
          <div class=card>
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Vendor Charges') }}</h5>
            </div>
            <div class="card-body p-0">
              <VendorChargesTable/>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="col-md-6">
          <div class="row">
            <div v-if="reservation.lot" class="col-md-12">
              <div class="card" v-if="reservation.lot">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Commissions') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="onCommissionsModalShowClick()">
                    <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 border-right">
                      <commissions-view-table :commissions="reservation.lot.financial_item.commission_tiers"
                                              :tax-rate="reservation.lot.financial_item.tax_rate"/>
                    </div>
                    <div class="col-md-6">
                      <table class="info-table table mb-0 table-sm">
                        <tr>
                          <td class="border-top-0 font-weight-bold">{{ $t('Minimum commission') }}</td>
                          <td class="border-top-0">{{ reservation.lot.financial_item.minimum_commission | asCurrency }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('L/D Warranty') }}</td>
                          <td>{{ reservation.lot.financial_item.insurance_rate | asPercentage }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('Entry Fee') }}</td>
                          <td>{{ reservation.lot.financial_item.lot_handling_charge | asCurrency }}
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold">{{ $t('Rebate') }}</td>
                          <td>{{ reservation.lot.financial_item.rebate_rate | asPercentage }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="reservation.lot" class="col-md-12 mt-3">
              <div class="card" v-if="reservation.lot">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Premiums') }}
                  </h5>
                  <b-button v-permission="'change_auction'" class="float-right p-0" variant="link"
                            @click="onPremiumsModalShowClick()">
                    <font-awesome-icon icon="pen" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <div class="row">
                    <premiums-display-table :premiums="lotPremiums"
                                            :minimum_premium="reservation.lot.financial_item.minimum_premium"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-12 mt-3" v-if="reservation.lot">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Tax Status') }}
                  </h5>
                  <b-button class="float-right p-0" variant="link"
                            @click="onShowTaxStatusModalClick(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <span>{{ currentTaxStatusName }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6 mt-3 mt-md-0">
          <div class="row" v-if="reservation.lot">
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">{{ $t("Charges") }}</h5>
                </div>
                <div class="card-body">
                  <table class="table table-sm" v-if="reservation.lot.financial_item.financial_item_charges.length > 0">
                    <tr>
                      <th>{{ $t("Title") }}</th>
                      <th>{{ $t("Amount") }}</th>
                      <th>{{ isSalesTaxEnabled ? $t('Sales Tax') : $t('VAT') }}</th>
                      <th></th>
                    </tr>
                    <tr v-for="(financial_item_charge,index) in reservation.lot.financial_item.financial_item_charges"
                        :key="`charge-row-${index}`">
                      <td>
                        {{ financial_item_charge.charge.name }}
                      </td>
                      <td>
                        {{ financial_item_charge.amount }}
                      </td>
                      <td>
                        {{ financial_item_charge.vat }}
                      </td>
                      <td class="text-right">

                        <b-tooltip :target="`charge-edit-btn-${financial_item_charge.uuid}`"
                                   :title="$t('Edit Charge')"/>
                        <b-button :id="`charge-edit-btn-${financial_item_charge.uuid}`" variant="primary" size="sm"
                                  class="mr-2"
                                  @click="onChargeEditClick(financial_item_charge)" v-permission="'change_auction'">
                          <font-awesome-icon icon="pen"></font-awesome-icon>
                        </b-button>
                      </td>
                    </tr>
                  </table>
                  <no-data-available v-else height="5"/>
                </div>
              </div>
            </div>
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">
                    {{ $t('Tax Rate') }}
                  </h5>
                  <b-button class="float-right p-0" variant="link"
                            @click="onShowTaxRateModalClick(true)">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <span>{{ reservation.lot.financial_item.tax_rate }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-12 mt-3" v-if="reservation.lot.increments.length || sale.increments.length">
              <div class="card">
                <div class="card-header">
                  <h5 class="d-inline-block mb-0">{{ $t("Increments") }}</h5>
                  <b-button class="float-right p-0" variant="link"
                            @click="showLotIncrementsModal({show: true, sale: sale, increments, lot: reservation.lot})">
                    <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('Edit') }}
                  </b-button>
                </div>
                <div class="card-body">
                  <table class="info-table table table-sm" v-if="sale.increment_calculation_source === 'bid'">
                    <tr>
                      <th>
                        {{ $t('Increment') }}
                      </th>
                      <th>
                        {{ $t('Upto') }}
                      </th>
                    </tr>
                    <tr
                      v-for="(increment, index) in reservation.lot.increments.length ? reservation.lot.increments : increments"
                      :key="`increment-row-${index}`">
                      <td>
                        {{ increment.increment | asCurrency(sale.currency.symbol) }}
                      </td>
                      <td>
                        {{ (increments.length - 1) === index ? $t('And Up') : increment.max }}
                      </td>
                    </tr>
                  </table>
                  <div v-else-if="sale.increment_calculation_source === 'reserve'">
                    {{ $t('Step:') }} {{ getIncrementStep() | asCurrency(sale.currency.symbol) }}
                  </div>
                  <div v-if="!reservation.lot.increments.length">
                    <small>{{ $t('Sale increments are applied') }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-12 mt-3">
          <div class=card>
            <div class="card-header">
              <h5 class="mb-0">{{ $t('Vendor Charges') }}</h5>
            </div>
            <div class="card-body p-0">
              <VendorChargesTable/>
            </div>
          </div>
        </div>
      </template>

    </div>
    <commissions-modal/>
    <financial-item-premiums-modal/>
    <increments-modal/>
    <premiums-modal/>
    <payment-terms-modal/>
    <settlement-days-modal/>
    <tax-status-modal/>
    <tax-rate-modal/>
    <lot-increments-modal/>
  </div>
</template>

<script>
  import Spinner from "@/core/components/common/Spinner";
  import NoDataAvailable from "../../../../core/components/common/NoDataAvailable";
  import {createNamespacedHelpers} from "vuex";
  import cloneDeep from "lodash/cloneDeep";
  import PaymentTermsModal from "@/modules/reservations/view/sale-settings/modals/PaymentTermsModal";
  import SettlementDaysModal from "@/modules/reservations/view/sale-settings/modals/SettlementDaysModal";
  import PremiumsModal from "@/modules/reservations/view/sale-settings/modals/PremiumsModal";
  import FinancialItemPremiumsModal from "@/modules/reservations/view/sale-settings/modals/FinancialItemPremiumsModal";
  import AuctionSettingsModal from "@/modules/sales/view/terms/modals/AuctionSettingsModal";
  import IncrementsModal from "@/modules/reservations/view/sale-settings/modals/IncrementsModal";
  import {SOURCE_RESERVATION_SALE_SETTINGS} from "@/modules/constants";
  import CommissionsViewTable from "@/modules/receipt/view/dashboard/CommissionsViewTable";
  import CommissionsModal from "./modals/CommissionsModal";
  import TaxStatusModal from "@/modules/reservations/view/sale-settings/modals/TaxStatusModal";
  import helperService from "@/core/services/helper.service";
  import LotIncrementsModal from "@/modules/reservations/view/sale-settings/modals/LotIncrementsModal.vue";
  import TaxRateModal from "@/modules/reservations/view/sale-settings/modals/TaxRateModal";
  import PremiumsDisplayTable from "@/modules/sales/view/terms/PremiumsDisplayTable";
  import VendorChargesTable from "@/modules/vendor-charges/VendorChargesTable";

  const {mapState, mapActions} = createNamespacedHelpers('reservations');
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: mapSaleActions} = createNamespacedHelpers('sales');
  const {mapActions: vendorChargesMapActions} = createNamespacedHelpers('vendorCharges');

  export default {
    name: "SaleSettingsView",
    components: {
      PremiumsDisplayTable,
      LotIncrementsModal,
      TaxStatusModal,
      TaxRateModal,
      CommissionsModal,
      FinancialItemPremiumsModal,
      CommissionsViewTable,
      Spinner,
      IncrementsModal,
      NoDataAvailable,
      PaymentTermsModal,
      SettlementDaysModal,
      PremiumsModal,
      AuctionSettingsModal,
      VendorChargesTable
    },
    data() {
      return {
        SOURCE_RESERVATION_SALE_SETTINGS: SOURCE_RESERVATION_SALE_SETTINGS,
      }
    },
    computed: {
      ...mapState({
        reservation: state => state.view.currentReservation,
        sale: state => state.view.currentReservation.auction,
        loading: state => state.view.settings.loading,
        increments: state => state.view.incrementsData.increments,
        premiums: state => {
          const taxRate = state.view.currentReservation.lot && state.view.currentReservation.lot.financial_item
            ? state.view.currentReservation.lot.financial_item.tax_rate
            : null
          let data = helperService.reverseCalculateTiers({premiums: cloneDeep(state.view.termsData.premiums)}, taxRate)
          return data.premiums
        },
        minimum_premium: state => state.view.termsData.minimum_premium,
        payment_terms: state => state.view.termsData.payment_terms,
        settlement_days: state => state.view.termsData.settlement_days,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        lotDelaySecondsOptions: state => state.lotDelaySecondsOptions,
        buyNowCutOffDaysOptions: state => state.buyNowCutOffDaysOptions,
        biddingExtensionOptions: state => state.biddingExtensionOptions,
        maxTotalDelayOptions: state => state.maxTotalDelayOptions,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        taxStatuses: state => state.taxStatuses,
      }),
      lotPremiums() {
        if (this.reservation && this.reservation.lot && this.reservation.lot.financial_item) {
          let data = helperService.reverseCalculateTiers({
            premiums: cloneDeep(this.reservation.lot.financial_item.premium_tiers)
          }, this.reservation.lot.financial_item.tax_rate)
          return data.premiums
        }
        return []
      },
      lotDelaySecondsValue() {
        let value = this.lotDelaySecondsOptions.find(i => i.value === this.sale.lot_delay_seconds)
        return value ? value.key : '';
      },
      buyNowCutOffDaysValue() {
        let value = this.buyNowCutOffDaysOptions.find(i => i.value === this.sale.buy_now_cut_off_days)
        return value ? value.key : this.$t("No 'Buy Now' option");
      },
      biddingExtensionValue() {
        let value = this.biddingExtensionOptions.find(i => i.value === this.sale.bidding_extension)
        return value ? value.key : this.$t("No Extension");
      },
      maxTotalDelayValue() {
        let value = this.maxTotalDelayOptions.find(i => i.value === this.sale.max_total_delay)
        return value ? value.key : this.$t("No 'Max Total Delay' option");
      },
      currentTaxStatusName() {
        if (this.reservation.lot && this.reservation.lot.financial_item) {
          const taxStatus = this.taxStatuses.find(status => status.uuid === this.reservation.lot.financial_item.tax_status_uuid);
          return taxStatus ? taxStatus.name : '';
        }
        return ''
      },
    },
    methods: {
      ...mapActions([
        'getIncrements',
        'getTermsData',
        'togglePaymentTermsModal',
        'toggleSettlementDaysModal',
        'togglePremiumsModal',
        'toggleIncrementsModal',
        'updateSale',
        'showCommissionsModal',
        'showFinancialItemPremiumsModal',
        'showFinancialItemChargeModal',
        'showTaxStatusModal',
        'showLotIncrementsModal',
        'showTaxRateModal',
      ]),
      ...mapSaleActions([
        'showAuctionSettingsModal',
      ]),
      ...vendorChargesMapActions({
        updateVendorChargesTableSource: 'updateTableSource',
        getVendorChargesTableData: 'getTableData'
      }),
      onShowAuctionSettingsModal() {
        this.showAuctionSettingsModal({
          object: this.sale,
          source: SOURCE_RESERVATION_SALE_SETTINGS,
          auction_uuid: this.sale.uuid
        });
      },
      onCommissionsModalShowClick() {
        this.showCommissionsModal({
          object: {
            commissions: this.reservation.lot.financial_item.commission_tiers,
            minimum_commission: this.reservation.lot.financial_item.minimum_commission,
            lot_handling_charge: this.reservation.lot.financial_item.lot_handling_charge,
            insurance_rate: this.reservation.lot.financial_item.insurance_rate,
            rebate_rate: this.reservation.lot.financial_item.rebate_rate,
          },
          reservation: this.reservation,
          source: SOURCE_RESERVATION_SALE_SETTINGS
        })
      },
      onChargeEditClick(financialItemCharge) {
        this.showFinancialItemChargeModal({
          object: financialItemCharge,
          source: SOURCE_RESERVATION_SALE_SETTINGS
        })
      },
      onShowTaxStatusModalClick() {
        this.showTaxStatusModal({
          object: {
            tax_status_uuid: this.reservation.lot.financial_item.tax_status_uuid,
            lot_uuid: this.reservation.lot.uuid
          }
        })
      },
      getIncrementStep() {
        return helperService.getLotIncrementStep(this.reservation.lot, this.sale.increments);
      },
      onShowTaxRateModalClick() {
        this.showTaxRateModal({
          object: {
            tax_rate: this.reservation.lot.financial_item.tax_rate,
            lot_uuid: this.reservation.lot.uuid
          }
        })
      },
      onPremiumsModalShowClick() {
        this.showFinancialItemPremiumsModal({
          object: {
            premiums: this.reservation.lot.financial_item.premium_tiers,
            minimum_premium: this.reservation.lot.financial_item.minimum_premium,
          },
          reservation: this.reservation,
          source: SOURCE_RESERVATION_SALE_SETTINGS
        })
      }
    },
    async beforeMount() {
      await this.updateVendorChargesTableSource({
        source: 'lot',
        sourceUuid: this.reservation.lot.uuid
      });
    },
    mounted() {
      this.getIncrements();
      this.getTermsData();
      this.getVendorChargesTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../scss/variables";

  .timed-auction-settings-card-body {
    max-height: 20em;
    overflow: auto;
  }
</style>
